"use client";

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatTimeDisplay } from "@/lib/utils/time";
import {
  getStatusColorConfig,
  getStatusColorClasses,
} from "@/lib/constants/statuses";
import { BOOKING_TYPE_IDS } from "@/lib/constants/booking-types";

interface SearchDropdownProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  bookings: any[];
  resources?: any[]; // Studios
  onBookingSelect: (bookingId: string) => void;
  /** Time format; left out it's 24-hour (formatTimeDisplay's default). */
  locale?: string;
  venueId: string;
  /** Width of the search box; defaults to w-80. */
  className?: string;
}

interface SearchResult {
  id: string;
  bookingNumber: string;
  customerName: string;
  customerEmail: string | null;
  companyName: string | null;
  showName: string;
  studioName: string;
  date: string;
  startTime: string;
  endTime: string;
  statusId: number;
  statusName: string;
  bookingTypeId?: number;
  relevance: number;
}

export default function SearchDropdown({
  searchQuery,
  onSearchChange,
  bookings,
  resources = [],
  onBookingSelect,
  locale,
  venueId,
  className,
}: SearchDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [allBookings, setAllBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const fetchedAllBookingsRef = useRef(false);

  // Pull the venue's full booking set once when the user first starts
  // searching, then filter client-side on each keystroke. Fetching only
  // the current calendar view would miss future- or past-dated bookings;
  // the underlying API filters by venue at the DB level so this is bounded
  // by venue size, not the global table.
  useEffect(() => {
    if (searchQuery.trim().length < 2 || fetchedAllBookingsRef.current) {
      return;
    }

    fetchedAllBookingsRef.current = true;
    setLoading(true);

    fetch(`/api/staff/venues/${venueId}/bookings`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setAllBookings(data.data.events || []);
        }
      })
      .catch((error) => {
        console.error("Error fetching bookings for search:", error);
        // Reset so a later attempt can retry.
        fetchedAllBookingsRef.current = false;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [searchQuery, venueId]);

  // Calculate search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      return [];
    }

    const query = searchQuery.toLowerCase().trim();
    const results: SearchResult[] = [];

    // Search the fetched venue dataset; fall back to the calendar's
    // current-view bookings if the bulk fetch hasn't completed yet so the
    // dropdown isn't blank during the initial load.
    const bookingsToSearch = allBookings.length > 0 ? allBookings : bookings;
    const seenBookingIds = new Set<string>();

    bookingsToSearch.forEach((booking) => {
      // Support both event format (with extendedProps) and raw booking format
      const props = booking.extendedProps || booking;

      const bookingNumber = props.bookingNumber || booking.booking_number || "";
      const customerName = props.customerName || "Unknown";
      const customerEmail = props.customerEmail || null;
      const companyName = props.companyName || null;
      const showName = props.showName || "No show";
      const studioId = props.studioId;
      const studioName = props.studioName || "Unknown studio";

      // Extract date from start property (ISO datetime) or from stored booking date
      let date = props.bookingDate ||
                 booking.booking_date ||
                 (booking.start ? booking.start.split('T')[0] : "");

      // Extract time from extendedProps (stored as HH:MM format)
      const startTime = props.startTime || "";
      const endTime = props.endTime || "";

      const statusId = props.statusId;
      const statusName = props.statusName || "Unknown";
      const bookingTypeId = props.bookingTypeId;

      // Calculate relevance score — flat tiers, date is the tiebreaker.
      let relevance = 0;

      // Exact match is most relevant
      if (bookingNumber.toLowerCase() === query) {
        relevance = 1000;
      }
      // Booking number contains query
      else if (bookingNumber.toLowerCase().includes(query)) {
        relevance = 500;
      }
      // Email exact match (full email)
      else if (customerEmail && customerEmail.toLowerCase() === query) {
        relevance = 450;
      }
      // Email contains query (partial email, domain name, etc)
      else if (customerEmail && customerEmail.toLowerCase().includes(query)) {
        relevance = 400;
      }
      // Customer name exact match
      else if (customerName.toLowerCase() === query) {
        relevance = 350;
      }
      // Customer name starts with query
      else if (customerName.toLowerCase().startsWith(query)) {
        relevance = 300;
      }
      // Customer name contains query
      else if (customerName.toLowerCase().includes(query)) {
        relevance = 200;
      }
      // Company name contains query
      else if (
        companyName &&
        companyName.toLowerCase().includes(query)
      ) {
        relevance = 150;
      }
      // Show name contains query
      else if (showName.toLowerCase().includes(query)) {
        relevance = 100;
      }

      if (relevance > 0) {
        // The event id is `<bookingId>-<timeslot>-<studioId>`; a single
        // booking with multiple shows/studios shows up as several events
        // with the same `bookingId`. Deduplicate so the dropdown shows
        // each booking once (avoids the React duplicate-key warning and
        // confusing repeated rows for the same booking).
        const bookingId = booking.id?.toString();
        const baseBookingId = bookingId?.includes("-")
          ? bookingId.split("-")[0]
          : bookingId;
        if (!baseBookingId || seenBookingIds.has(baseBookingId)) {
          return;
        }
        seenBookingIds.add(baseBookingId);

        results.push({
          id: baseBookingId,
          bookingNumber,
          customerName,
          customerEmail,
          companyName,
          showName,
          studioName,
          date,
          startTime,
          endTime,
          statusId,
          statusName,
          bookingTypeId,
          relevance,
        });
      }
    });

    // Sort by relevance (desc), then group same-customer bookings together,
    // then newest first within each customer. Grouping keeps a customer's
    // multiple bookings adjacent in the dropdown instead of getting split
    // apart by other matches.
    return results.sort((a, b) => {
      if (b.relevance !== a.relevance) {
        return b.relevance - a.relevance;
      }
      const aCustomer = (a.customerName || "").toLowerCase();
      const bCustomer = (b.customerName || "").toLowerCase();
      if (aCustomer !== bCustomer) {
        return aCustomer.localeCompare(bCustomer);
      }
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [searchQuery, allBookings, bookings]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) {
        if (
          (e.key === "ArrowDown" || e.key === "Enter") &&
          searchResults.length > 0
        ) {
          setIsOpen(true);
          setSelectedIndex(0);
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < searchResults.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && searchResults[selectedIndex]) {
            onBookingSelect(searchResults[selectedIndex].id);
            setIsOpen(false);
            setSelectedIndex(-1);
            onSearchChange("");
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setSelectedIndex(-1);
          break;
      }
    },
    [isOpen, selectedIndex, searchResults, onBookingSelect, onSearchChange]
  );

  // Open dropdown whenever there's something to show (results, loading
  // state, or the "no matches" message). Closing only on length < 2
  // means the user gets feedback instead of a silently empty dropdown.
  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      setIsOpen(true);
      setSelectedIndex(searchResults.length > 0 ? 0 : -1);
    } else {
      setIsOpen(false);
      setSelectedIndex(-1);
    }
  }, [searchQuery, searchResults]);

  const getStatusBadgeColor = (statusId: number) => {
    const colorConfig = getStatusColorConfig(statusId);
    return colorConfig.textColor;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "Invalid Date";
    try {
      // Handle various date formats
      let date: Date;

      // Format: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS
      if (dateStr.includes("-")) {
        const parts = dateStr.split("T")[0]; // Get just the date part if it's ISO
        const [year, month, day] = parts.split("-");
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        date = new Date(dateStr);
      }

      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (e) {
      return "Invalid Date";
    }
  };

  const safeFormatTime = (timeStr: string | undefined) => {
    if (!timeStr) return "00:00";
    // Handle formats like "HH:MM:SS" or "HH:MM"
    const parts = timeStr.split(":");
    if (parts.length >= 2) {
      const hours = parts[0];
      const minutes = parts[1];
      return formatTimeDisplay(`${hours}:${minutes}`, locale);
    }
    return "00:00";
  };

  return (
    <div ref={containerRef} className={cn("relative", className ?? "w-80")}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search booking, name or email"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchQuery.trim().length >= 2) {
              setIsOpen(true);
            }
          }}
          className="pl-10 pr-8 w-full"
        />
        {searchQuery && (
          <button
            onClick={() => {
              onSearchChange("");
              setIsOpen(false);
              setSelectedIndex(-1);
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && searchResults.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
        >
          <div className="py-2">
            {searchResults.map((result, index) => (
              <div
                key={result.id}
                onClick={() => {
                  onBookingSelect(result.id);
                  setIsOpen(false);
                  setSelectedIndex(-1);
                  onSearchChange("");
                }}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`px-4 py-3 cursor-pointer transition-colors ${
                  selectedIndex === index
                    ? "bg-blue-50"
                    : "hover:bg-gray-50"
                }`}
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Booking Number & Status */}
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-sm text-gray-900 truncate">
                        {result.bookingNumber}
                      </p>
                      <Badge
                        variant="secondary"
                        className="flex-shrink-0 text-xs"
                        style={{
                          color: getStatusBadgeColor(result.statusId),
                        }}
                      >
                        {result.statusName}
                      </Badge>
                    </div>

                    {/* Customer Info */}
                    <div className="text-xs text-gray-600 mb-2">
                      <p className="truncate font-medium">
                        {result.customerName}
                      </p>
                      {result.customerEmail && (
                        <p className="text-gray-500 truncate text-xs">
                          {result.customerEmail}
                        </p>
                      )}
                      {result.companyName && (
                        <p className="text-gray-500 truncate">
                          {result.companyName}
                        </p>
                      )}
                    </div>

                    {/* Show & Studio */}
                    {result.bookingTypeId !== BOOKING_TYPE_IDS.ON_ROAD && (
                      <div className="text-xs text-gray-600 mb-1">
                        <p className="truncate">
                          <span className="font-medium">{result.showName}</span>
                          <span className="text-gray-400"> • </span>
                          <span>{result.studioName}</span>
                        </p>
                      </div>
                    )}

                    {/* Date & Time */}
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <span>{formatDate(result.date)}</span>
                      <span className="text-gray-400">•</span>
                      <span>
                        {safeFormatTime(result.startTime)} -{" "}
                        {safeFormatTime(result.endTime)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty / loading state */}
      {isOpen &&
        searchQuery.trim().length >= 2 &&
        searchResults.length === 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="px-4 py-8 text-center">
              <Search className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">
                {loading
                  ? `Searching for "${searchQuery}"…`
                  : `No bookings found for "${searchQuery}"`}
              </p>
            </div>
          </div>
        )}
    </div>
  );
}
